import { useRef, useState } from 'react';
import type { AppState, Barcode, BarcodeLookup, Can } from '../../shared/api';
import { useCorrectBarcode } from '../api';
import { CanSearch } from '../components/CanSearch';
import { Sheet } from '../components/Sheet';
import { VariantPicker } from '../components/VariantPicker';
import { foundVariant, ScanCan } from './PublicScan';

/** Correct the catalog mapping independently of the collection add flow. */
export function BarcodeCorrectionSheet({
  state,
  previous,
  onClose,
  onSaved,
}: {
  state: AppState;
  previous: BarcodeLookup;
  onClose: () => void;
  onSaved: (barcode: Barcode) => void;
}) {
  const [can, setCan] = useState<Can | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [sizeMl, setSizeMl] = useState<number | null>(null);
  const [error, setError] = useState('');
  const saved = useRef<Barcode | null>(null);
  const saving = useRef(false);
  const correction = useCorrectBarcode();
  const previousCan = state.cans.find((item) => item.id === previous.canId);
  return (
    <Sheet
      label="EAN-koppeling corrigeren"
      onClose={() => {
        if (saved.current) onSaved(saved.current);
        else onClose();
      }}
    >
      {(close) => (
        <>
          <div className="label label--green">Streepjescode</div>
          <h2 className="sheet__name">Koppeling corrigeren</h2>
          <p className="note">
            EAN <span className="scan-can__ean">{previous.ean}</span>
            <br />
            Nu gekoppeld aan {previousCan?.name ?? 'onbekend blik'} · {foundVariant(previous)}.
          </p>
          <p className="card__text">
            Kies het blik dat bij deze code hoort. Dit wijzigt de gedeelde catalogus. Je bestaande collectie blijft
            gelijk.
          </p>
          <CanSearch
            cans={state.cans}
            categories={state.categories}
            onPick={setCan}
            label="Kies de juiste smaak"
            placeholder="Zoek de juiste smaak"
            autoFocus
          />
          {can && (
            <>
              <ScanCan can={can} barcode={{ ...previous, canId: can.id, country, sizeMl }} />
              <VariantPicker
                optional
                nullLabel="Onbekend"
                country={country}
                sizeMl={sizeMl}
                onCountry={setCountry}
                onSize={setSizeMl}
              />
            </>
          )}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="scan-actions">
            <button
              type="button"
              className="btn"
              disabled={!can || correction.isPending}
              onClick={async () => {
                if (!can || saving.current) return;
                saving.current = true;
                setError('');
                try {
                  saved.current = await correction.mutateAsync({ ean: previous.ean, canId: can.id, country, sizeMl });
                  close();
                } catch (err) {
                  setError((err as Error).message);
                } finally {
                  saving.current = false;
                }
              }}
            >
              {correction.isPending ? 'Opslaan…' : 'Koppeling opslaan'}
            </button>
            <button type="button" className="text-link" onClick={close}>
              Annuleren
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}
